import React from 'react';
import { Flight } from '../../types/Quotation';
import { formatDate, formatTime } from '../../utils/date';

interface FlightCardProps {
  flight: Flight & { day: number };
}

const FlightCard: React.FC<FlightCardProps> = ({ flight }) => {
  const segments = Array.isArray(flight.segments) ? flight.segments : [];
  const carrierCode = (flight.provider || '').trim();
  const carrierLogoCode = carrierCode.toUpperCase();
  const carrierLabel = (carrierCode || flight.airline || 'Air').trim();
  const carrierInitials = carrierLabel
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('') || '✈';

  const getFlightClassIcon = (flightClass?: string) => {
    if (!flightClass) return '✈️';
    const classLower = flightClass.toLowerCase();
    if (classLower.includes('business')) return '💼';
    if (classLower.includes('first')) return '👑';
    if (classLower.includes('economy')) return '✈️';
    return '✈️';
  };

  const getStopsInfo = (stops: number) => {
    if (stops === 0) return { text: 'Non-stop', color: '#27ae60' };
    if (stops === 1) return { text: '1 stop', color: '#f39c12' };
    return { text: `${stops} stops`, color: '#e74c3c' };
  };

  const formatAirportLabel = (name?: string, code?: string) => {
    const safeName = (name || '').trim();
    const safeCode = (code || '').trim();
    if (!safeName && !safeCode) return '';
    if (safeName && safeCode) {
      const lowerName = safeName.toLowerCase();
      const lowerCode = safeCode.toLowerCase();
      return lowerName.includes(lowerCode) ? safeName : `${safeName} (${safeCode})`;
    }
    return safeName || safeCode;
  };

  const formatDurationMins = (mins: number) => {
    if (!Number.isFinite(mins) || mins <= 0) return '';
    const hours = Math.floor(mins / 60);
    const minutes = mins % 60;
    return `${hours}h${minutes ? ` ${minutes}m` : ''}`;
  };

  const getLayovers = () => {
    if (segments.length < 2) return [];

    return segments
      .slice(0, -1)
      .map((segment: any, index: number) => {
        const nextSegment: any = segments[index + 1];
        if (!segment?.arrivalTime || !nextSegment?.departureTime) return null;

        const arrivalTime = new Date(segment.arrivalTime).getTime();
        const departureTime = new Date(nextSegment.departureTime).getTime();
        if (!Number.isFinite(arrivalTime) || !Number.isFinite(departureTime)) return null;

        const durationMins = Math.max(0, Math.round((departureTime - arrivalTime) / 60000));
        return {
          airport: formatAirportLabel(segment.toAirportName, segment.toCode || segment.destination),
          duration: formatDurationMins(durationMins),
        };
      })
      .filter(Boolean) as Array<{ airport: string; duration: string }>;
  };

  const stopsInfo = getStopsInfo(flight.stops);
  const layovers = getLayovers();
  const baggageDetails = [
    flight.cabinBaggage ? { label: 'Cabin baggage', value: flight.cabinBaggage } : null,
    flight.checkInBaggage ? { label: 'Check-in baggage', value: flight.checkInBaggage } : null,
    !flight.cabinBaggage && !flight.checkInBaggage && flight.baggage
      ? { label: 'Baggage', value: flight.baggage }
      : null,
  ].filter(Boolean) as Array<{ label: string; value: string }>;
  const seatInfo = flight.seat || flight.seatNumber || flight.seatSelection || '';

  return (
    <div style={{
      border: '1px solid #e1e5e9',
      borderRadius: '12px',
      padding: '16px',
      marginBottom: '16px',
      backgroundColor: '#ffffff',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      display: 'flex',
      gap: '16px'
    }}>
      {/* Airline Logo */}
      <div style={{ flex: '0 0 80px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ position: 'relative', width: '48px', height: '48px', marginBottom: '8px' }}>
          {carrierCode ? (
            <img
              src={`https://cdn.otaprime.com/airline/${encodeURIComponent(carrierLogoCode)}.gif`}
              alt={flight.airline}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain'
              }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
                (e.target as HTMLImageElement).nextElementSibling?.setAttribute('style', 'display: flex');
              }}
            />
          ) : null}
          {/* Fallback airline badge */}
            <div style={{
            width: '100%',
            height: '100%',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            display: carrierCode ? 'none' : 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: 700,
            color: '#2c3e50',
            border: '1px solid #e9ecef',
            position: 'absolute',
            top: 0,
            left: 0
          }}>
            {carrierInitials}
          </div>
        </div>
        <div style={{
          fontSize: '10px',
          color: '#6c757d',
          textAlign: 'center',
          lineHeight: '1.2'
        }}>
          {carrierLabel}
        </div>
      </div>

      {/* Flight Details */}
      <div style={{ flex: 1 }}>
        {/* Flight Number and Class */}
        <div style={{ marginBottom: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <span style={{ fontSize: '16px' }}>{getFlightClassIcon(flight.class)}</span>
            <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#2c3e50' }}>
              {flight.provider}-{flight.flightNumber}
            </h4>
            <span style={{
              fontSize: '11px',
              backgroundColor: '#f8f9fa',
              padding: '2px 6px',
              borderRadius: '8px',
              color: '#6c757d'
            }}>
              {flight.class || 'Economy'}
            </span>
          </div>
        </div>

        {/* Route Information */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          marginBottom: '12px'
        }}>
          {/* From */}
          <div style={{ textAlign: 'center', flex: 1 }}>
            <div style={{ fontSize: '18px', fontWeight: '600', color: '#2c3e50' }}>
              {flight.from}
            </div>
            <div style={{ fontSize: '12px', color: '#6c757d' }}>
              {formatAirportLabel(flight.fromAirportName, flight.from)}
            </div>
            <div style={{ fontSize: '14px', fontWeight: '500', color: '#34495e', marginTop: '4px' }}>
              {formatTime(flight.departureTime)}
            </div>
          </div>

          {/* Flight Path */}
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            flex: '0 0 120px',
            padding: '0 16px'
          }}>
            <div style={{
              width: '100%',
              height: '2px',
              backgroundColor: '#bdc3c7',
              position: 'relative',
              marginBottom: '8px'
            }}>
              <div style={{
                position: 'absolute',
                right: '0',
                top: '-4px',
                width: '0',
                height: '0',
                borderLeft: '8px solid #bdc3c7',
                borderTop: '4px solid transparent',
                borderBottom: '4px solid transparent'
              }} />
            </div>
            <div style={{ 
              fontSize: '11px', 
              color: '#6c757d',
              textAlign: 'center',
              marginBottom: '4px'
            }}>
              {flight.duration}
            </div>
            <div style={{
              fontSize: '10px',
              backgroundColor: stopsInfo.color,
              color: 'white',
              padding: '2px 6px',
              borderRadius: '8px',
              fontWeight: '500'
            }}>
              {stopsInfo.text}
            </div>
          </div>

          {/* To */}
          <div style={{ textAlign: 'center', flex: 1 }}>
            <div style={{ fontSize: '18px', fontWeight: '600', color: '#2c3e50' }}>
              {flight.to}
            </div>
            <div style={{ fontSize: '12px', color: '#6c757d' }}>
              {formatAirportLabel(flight.toAirportName, flight.to)}
            </div>
            <div style={{ fontSize: '14px', fontWeight: '500', color: '#34495e', marginTop: '4px' }}>
              {formatTime(flight.arrivalTime)}
            </div>
          </div>
        </div>

        {/* Additional Info */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: baggageDetails.length > 0 || seatInfo ? 'repeat(3, minmax(0, 1fr))' : '1fr 1fr',
          gap: '12px',
          padding: '12px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px'
        }}>
          <div>
            <div style={{ fontSize: '10px', color: '#6c757d', marginBottom: '2px' }}>Date</div>
            <div style={{ fontSize: '12px', fontWeight: '500' }}>
              {formatDate(flight.departureTime)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '10px', color: '#6c757d', marginBottom: '2px' }}>Aircraft</div>
            <div style={{ fontSize: '12px', fontWeight: '500' }}>
              {flight.aircraft || 'N/A'}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '10px', color: '#6c757d', marginBottom: '2px' }}>Seat</div>
            <div style={{ fontSize: '12px', fontWeight: '500' }}>
              {seatInfo || 'Not assigned'}
            </div>
          </div>
        </div>

        {baggageDetails.length > 0 && (
          <div style={{
            marginTop: '12px',
            padding: '12px',
            backgroundColor: '#eef7ff',
            border: '1px solid #cfe3f8',
            borderRadius: '8px'
          }}>
            <div style={{
              fontSize: '10px',
              color: '#35607d',
              marginBottom: '6px',
              textTransform: 'uppercase',
              letterSpacing: '0.04em'
            }}>
              Baggage
            </div>
            <div style={{ display: 'grid', gap: '6px' }}>
              {baggageDetails.map((item) => (
                <div key={item.label} style={{ fontSize: '12px', color: '#2f566f' }}>
                  <strong>{item.label}:</strong> {item.value}
                </div>
              ))}
            </div>
          </div>
        )}

        {layovers.length > 0 && (
          <div style={{
            marginTop: '12px',
            padding: '12px',
            backgroundColor: '#fff7e6',
            border: '1px solid #f3d19c',
            borderRadius: '8px'
          }}>
            <div style={{ fontSize: '10px', color: '#8a6d3b', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Layover{layovers.length > 1 ? 's' : ''}
            </div>
            <div style={{ display: 'grid', gap: '6px' }}>
              {layovers.map((layover, index) => (
                <div key={`${layover.airport}-${index}`} style={{ fontSize: '12px', color: '#6b4f1d' }}>
                  {layover.airport ? `Layover in ${layover.airport}` : 'Layover'}{layover.duration ? ` - ${layover.duration}` : ''}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FlightCard;
